
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { formatOrderNumber } from '@/utils/format';
import CartItemList from '@/components/cart/CartItemList.vue';
import CartPricing from '@/components/cart/CartPricing.vue';
import Modal from '@/components/shared/Modal.vue';
import OrderModificationRequestModal from '@/components/profile/OrderModificationRequestModal.vue';
import WarningModal from '@/components/shared/WarningModal.vue';

const namespace: string = 'auth';

@Component<ScheduledOrdersDetails>({
	components: {
		CartItemList,
		CartPricing,
		Modal,
		OrderModificationRequestModal,
		WarningModal
	}
})
export default class ScheduledOrdersDetails extends Vue {
	@Action('setSelectedScheduledOrder', { namespace }) private setSelectedScheduledOrder!: (order: Order | null) => void;
	@Action('fetchScheduledOrders', { namespace }) private fetchScheduledOrders!: (payload: ScheduledOrdersPayload) => Order[];
	@Action('cancelScheduledOrder', { namespace }) private cancelScheduledOrder!: (payload: CancelOrderPayload) => Promise<void>;
	@Getter('getScheduledOrders', { namespace }) private orders!: Order[];
	@Getter('getSelectedScheduledOrder', { namespace }) private order!: Order;
	@Getter('showLoginAsBanner', { namespace }) private showLoginAsBanner!: boolean;
	@Getter('getUser', { namespace }) private user!: UserInfo;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Getter('modifyRequestsEnabled', { namespace: 'restaurant' }) private modifyRequestsEnabled!: boolean;
	@Getter('isGenericCatering', { namespace: 'cart' }) private isGenericCatering!: boolean;

	private fetchingOrders: boolean = false;
	private cancelOrderModalOpen: boolean = false;
	private modificationRequestModalOpen: boolean = false;
	private cancellingOrder: boolean = false;

	private get formattedDate(): string {
		const dateFormat = this.order.type === 'school' ? 'DDD' : 'DDD, t';
		if (this.order.type === 'catering') {
			return DateTime.fromISO(this.order.pickup_time || this.order.data.suites!.eventStartDate).setLocale(this.$i18n.locale).toFormat(dateFormat);
		}
		return DateTime.fromISO(this.order.pickup_time).setLocale(this.$i18n.locale).toFormat(dateFormat);
	}

	/**
	 * Get the location name for the order
	 * If the order is a school order, return the school name if available
	 *
	 * @return {string}
	 */
	private get locationName(): string {
		const delivery = this.order.data.checkout.delivery;
		return delivery?.type === 'school' && delivery.school ? delivery.school : this.restaurant.name;
	}

	/**
	 * Get the delivery details for the order
	 * If the order is a school order, return the student, grade, classroom, and teacher
	 * Otherwise, return the address
	 * Return 'Pickup' if no delivery details are available
	 *
	 * @return {string}
	 */
	private get deliveryDetails(): string {
		const delivery = this.order.data.checkout.delivery;
		if (delivery) {
			if (delivery.type !== 'school') {
				return this.$options.filters?.localize(delivery.address!) || delivery.address!;
			}
			return [
				`${delivery.student}`,
				`${delivery.grade ? this.$t('profile.scheduled_orders.grade', { grade: delivery.grade }) : ''}`,
				`${delivery.classroom ? this.$t('profile.scheduled_orders.class', { class: delivery.classroom }) : ''}`,
				`${delivery.teacher ? `${delivery.teacher}` : ''}`,
				`${delivery.extra ? `${delivery.extra}` : ''}`
			].filter(Boolean).join(', ');
		}

		return this.$t('profile.scheduled_orders.pickup');
	}

	/**
	 * Check if the cancel button should be shown
	 * The cancel button is shown if the restaurant allows guest order cancellation,
	 * the order is a K12 order, the order status is 'in-progress',
	 * and the current time is before the cancellation cutoff
	 *
	 * @return {boolean}
	 */
	private get showCancelButton(): boolean {
		const zone = this.restaurant.zone || 'America/Toronto';
		const cutoffTime = DateTime.fromISO(this.order.pickup_time).setZone(zone).minus({ hours: this.restaurant.orderCancellationCutoffHours });
		const beforeCutoff = DateTime.now().setZone(zone) <= cutoffTime;

		return this.restaurant.guestOrderCancellation && this.restaurant.k12 && this.order.status === 'in-progress' && this.restaurant.creditsEnabled && this.restaurant.endOfSchoolYear && beforeCutoff;
	}

	/**
	 * Get the order credit expiry date
	 *
	 * @return {string}
	 */
	private get creditExpiryDate(): string | null {
		if (!this.restaurant.endOfSchoolYear) {
			return null;
		}
		return DateTime.fromISO(this.restaurant.endOfSchoolYear).setLocale(this.$i18n.locale).toFormat('DDD');
	}

	/**
	 * Check if the order modification request button should be shown
	 * The location must have modification requests enabled, be a generic catering order, and the location must be a generic catering location
	 *
	 * @return {boolean}
	 */
	private get showOrderModificationRequest(): boolean {
		return this.modifyRequestsEnabled && this.order.type === 'generic-catering';
	}

	/**
	 * Check if there is a pending modification request for the order
	 *
	 * @return {boolean}
	 */
	private get hasPendingRequest(): boolean {
		return this.order.modify_requests?.some((request: OrderModificationRequest) => request.status === 'pending') || false;
	}

	/**
	 * Check if the current date is after the modification request cutoff date
	 *
	 * @return {boolean}
	 */
	private get afterModificationRequestCutoffDate(): boolean {
		const cutoffHours = this.restaurant.modificationRequestCutoffHours || 0;
		const cutoffTime = DateTime.fromISO(this.order.pickup_time).minus({ hours: cutoffHours });

		// Ensure the current datetime is in the same timezone as the cutoff time for comparison
		return DateTime.now().setZone(cutoffTime.zone) > cutoffTime;
	}

	/**
	 * Format the order costs to be used in the CartPricing component
	 *
	 * @return {{ [key: string]: string | TotalBracketTaxes[] }}
	 */
	private get pricingProps(): { [key: string]: string | CreditsCosts | TotalBracketTaxes[] } {
		const costs = this.order.data.costs;
		return {
			creditsApplied: costs.credits,
			subtotal: costs.subtotal.toString(),
			promoDiscount: costs.discount.amount ? costs.discount.amount?.toString() : '',
			memberDiscount: costs.memberDiscount.amount ? costs.memberDiscount.amount?.toString() : '',
			serviceCharge: costs.serviceCharge.amount ? costs.serviceCharge.amount?.toString() : '',
			deliveryCharge: costs.deliveryCharge.amount ? costs.deliveryCharge.amount?.toString() : '',
			taxes: costs.taxes.toString(),
			total: costs.total.toString(),
			taxInfoProp: costs.taxInfo,
			tip: costs.tip.amount.toString(),
			voucherAmount: costs.voucher.amount ? costs.voucher.amount.toString() : '',
			totalWithTip: costs.totalWithTip.toString()
		};
	}

	/**
	 * Fetch scheduled orders for the user and set the selected order
	 * If there are no orders or the order isn't found, redirect to the scheduled orders page
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		if (!this.orders.length) {
			try {
				this.fetchingOrders = true;
				await this.fetchScheduledOrders({
					app8RestaurantId: this.restaurant.app8_restaurant,
					userId: this.user.id,
					token: this.user.token
				});
				const order = this.orders.find(order => order.id === this.$route.params.order_id);
				if (!order) {
					this.toScheduledOrders();
					return;
				}
				this.setSelectedScheduledOrder(order);
			}
			catch (error) {
				this.$toasted.show(this.$t('profile.scheduled_orders.error_fetching_order', { id: formatOrderNumber(this.$route.params.order_id, this.restaurant) }), { type: 'error', position: 'top-center' }).goAway(5000);
				this.toScheduledOrders();
			}
			finally {
				this.fetchingOrders = false;
			}
		}
	}

	/**
	 * Cancel the scheduled order
	 *
	 * @return {Promise<void>}
	 */
	private async cancelOrder(): Promise<void> {
		this.cancellingOrder = true;

		try {
			await this.cancelScheduledOrder({
				app8RestaurantId: this.restaurant.app8_restaurant,
				userId: this.user.id,
				token: this.user.token,
				orderId: this.order.id,
				expirationDate: this.restaurant.endOfSchoolYear
			});

			this.cancellingOrder = false;
			this.cancelOrderModalOpen = false;
			this.$toasted.show(this.$t('profile.scheduled_orders.order_cancelled_success'), { type: 'success', position: 'top-center' }).goAway(5000);
			this.toScheduledOrders();
		}
		catch (error) {
			this.cancellingOrder = false;
			this.cancelOrderModalOpen = false;
			const errorResponse = error.response?.data || {};
			this.$toasted.show(errorResponse?.message || this.$t('profile.scheduled_orders.error_cancelling_orders'), { type: 'error', position: 'top-center' }).goAway(5000);

			// If the error code is 443, it means the order was already cancelled and refunded to credits, so redirect back to scheduled orders page
			if (errorResponse?.code === 443) {
				this.toScheduledOrders();
			}
		}
	}

	/**
	 * Handle the submission of a modification request
	 *
	 * @param {OrderModificationRequest} newRequest - The new modification request
	 * @return {void}
	 */
	private handleModificationRequestSubmit(newRequest: OrderModificationRequest): void {
		this.order.modify_requests = this.order.modify_requests ? [...this.order.modify_requests, newRequest] : [newRequest];
		this.modificationRequestModalOpen = false;
	}

	/**
	 * Redirect to the scheduled orders page
	 *
	 * @return {void}
	 */
	private toScheduledOrders(): void {
		this.setSelectedScheduledOrder(null);
		this.$router.push({ path: '/profile/scheduled-orders', query: this.$route.query }).catch(() => {});
	}
}
